import { useRef, useState } from "react";
import { get } from "../../../apiClient";
import { action, URL_STATUS_KEY } from "../../../utils/urlsTabsHelper";

export const EPISODE_NOUN = { one: "episode", other: "episodes" };
export const URL_NOUN = { one: "URL", other: "URLs" };

export const countLabel = (count, noun) =>
  `${count} ${count === 1 ? noun.one : noun.other}`;

const pendingNoun = (noun) => ({
  one: `pending ${noun.one}`,
  other: `pending ${noun.other}`,
});

export const bulkEditButtonLabel = (count) =>
  count >= 2 ? `Bulk edit (${count})` : "Bulk edit";

export const bulkEditTitle = (count, noun) =>
  `Edit ${countLabel(count, pendingNoun(noun))}`;

export const bulkSaveLabel = (count, noun, status) => {
  if (status === "live") return `Set ${countLabel(count, noun)} to Live`;
  if (status === "rejected") return `Reject ${countLabel(count, noun)}`;
  return "Save";
};

export const CLEAR_SELECTION_LABEL = "Clear selection";

export const selectAllActionLabel = (totalCount, noun) =>
  `Select all ${countLabel(totalCount, noun)}`;

export const missingIdMessage = (count, noun) =>
  count === 1
    ? `1 ${noun.one} has no id and was left unselected.`
    : `${count} ${noun.other} have no id and were left unselected.`;

export function idKey(id) {
  if (typeof id === "number") return Number.isFinite(id) ? String(id) : null;
  if (typeof id === "string" && id.trim() !== "") return id.trim();
  return null;
}

const idForUpdate = (id) => {
  const key = idKey(id);
  if (key === null) return id;
  if (/^-?\d+$/.test(key)) {
    const numeric = Number(key);
    if (Number.isSafeInteger(numeric)) return numeric;
  }
  return key;
};

export const bulkSavedMessage = (written, remainder, noun) => {
  const updated = `Updated ${countLabel(written, noun)}.`;
  if (remainder <= 0) return updated;
  const pending = countLabel(remainder, pendingNoun(noun));
  return `${updated} ${pending} ${
    remainder === 1 ? "was" : "were"
  } not selected.`;
};

export const minimumSelectionHint = (noun) =>
  `Select at least 2 ${noun.other}.`;

const failureMessage = (noun) =>
  `Those ${noun.other} were not updated. Check the rating and status, then save again.`;

const permissionMessage = (noun) =>
  `You don't have permission to update these ${noun.other}.`;

const pageIdsOf = (records) =>
  (records ?? []).map((record) => idKey(record.id)).filter((id) => id !== null);

export function buildBulkUpdateList(checkedList, bulkEditParams) {
  let filteredParams = { ...bulkEditParams };

  if (bulkEditParams[URL_STATUS_KEY] === "live") {
    const { rejection_status, ...paramsWithoutRejectionStatus } =
      bulkEditParams;
    filteredParams = paramsWithoutRejectionStatus;
  }

  return checkedList.map((id) => ({ ...filteredParams, id: idForUpdate(id) }));
}

const isFailedResponse = (result) => {
  const status = result?.status;
  return typeof status === "number" && status >= 400 && status < 600;
};

async function fetchPendingRows({ campaignId, status, totalCount }) {
  const rows = [];
  const seen = new Set();
  let skipped = 0;
  let page = 0;
  let pageSize = Math.max(totalCount, 1);
  let pages = 0;

  while (rows.length < totalCount && page < 50) {
    const response = await get(
      "managed_plus",
      `/campaigns/${campaignId}/campaign_urls`,
      { params: { page, perPage: pageSize, status } }
    );
    const batch = response?.result ?? [];
    pages += 1;
    if (batch.length === 0) break;

    let added = 0;
    let unrecognized = 0;
    /* eslint-disable no-loop-func */
    batch.forEach((row) => {
      const id = idKey(row.id);
      if (id === null) {
        skipped += 1;
        unrecognized += 1;
        return;
      }
      if (seen.has(id)) return;
      seen.add(id);
      rows.push(row);
      added += 1;
    });
    /* eslint-enable no-loop-func */
    if (added === 0 && unrecognized === 0) break;

    const reported = response?.total_count ?? totalCount;
    if (rows.length >= reported) break;
    if (batch.length < pageSize) pageSize = batch.length;
    page += 1;
  }

  return { rows, pages, skipped };
}

export function useBulkUrlSelection({
  records,
  totalCount = 0,
  campaignId,
  status,
  noun,
  onForbiddenError,
}) {
  const [checkedList, setCheckedList] = useState([]);
  const [selectingAll, setSelectingAll] = useState(false);
  const [selectAllError, setSelectAllError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [notice, setNotice] = useState(null);
  const savingRef = useRef(false);
  const pageIds = pageIdsOf(records);
  const selectedOnPage = pageIds.filter((id) =>
    checkedList.includes(id)
  ).length;
  const headerChecked = pageIds.length > 0 && selectedOnPage === pageIds.length;
  const headerIndeterminate = selectedOnPage > 0 && !headerChecked;
  const allPendingSelected =
    totalCount > 0 && checkedList.length === totalCount;

  const togglePage = (event) => {
    const checked = event.target.checked;
    const next = checked
      ? Array.from(new Set([...checkedList, ...pageIds]))
      : checkedList.filter((id) => !pageIds.includes(id));
    setCheckedList(next);
  };

  const toggleRow = (event) => {
    const { value, checked } = event.target;
    const id = idKey(value);
    if (id === null) return;
    setCheckedList((current) => {
      if (checked) {
        return current.includes(id) ? current : [...current, id];
      }
      return current.filter((item) => item !== id);
    });
  };

  const removeChecked = (id) => {
    const key = idKey(id);
    setCheckedList((current) => current.filter((item) => item !== key));
  };

  const clearSelection = () => {
    setCheckedList([]);
    setSelectAllError(null);
  };

  const selectAllPending = async () => {
    if (selectingAll) return;
    setSelectingAll(true);
    setSelectAllError(null);
    try {
      const { rows, skipped } = await fetchPendingRows({
        campaignId,
        status,
        totalCount,
      });
      const ids = rows.map((row) => idKey(row.id)).filter((id) => id !== null);
      setCheckedList(ids);
      if (ids.length + skipped < totalCount) {
        setSelectAllError(
          `Couldn't load every pending ${noun.one}. Try again.`
        );
      } else if (skipped > 0) {
        setSelectAllError(missingIdMessage(skipped, noun));
      }
    } catch (error) {
      if (error?.status === 403) onForbiddenError?.();
      setSelectAllError(`Couldn't load every pending ${noun.one}. Try again.`);
    } finally {
      setSelectingAll(false);
    }
  };

  const selectQueue = () => {
    if (selectingAll || allPendingSelected) return;
    return selectAllPending();
  };

  const save = async ({
    params,
    campaignId: id,
    campaignStatus,
    onSuccess,
  }) => {
    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    setSaveError(null);
    const written = checkedList.length;
    const remainder = Math.max(totalCount - written, 0);
    try {
      const result = await action({
        urls_to_be_updated: JSON.stringify(
          buildBulkUpdateList(checkedList, params)
        ),
        campaignId: id,
        campaign_status: campaignStatus,
      });
      const failed = isFailedResponse(result);
      if (failed) {
        setSaveError(failureMessage(noun));
        return;
      }
      setCheckedList([]);
      setSelectAllError(null);
      setNotice(bulkSavedMessage(written, remainder, noun));
      onSuccess?.();
    } catch (error) {
      if (error?.status === 403) onForbiddenError?.();
      setSaveError(
        error?.status === 403 ? permissionMessage(noun) : failureMessage(noun)
      );
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  return {
    checkedList,
    selectedCount: checkedList.length,
    buttonLabel: bulkEditButtonLabel(checkedList.length),
    headerChecked,
    headerIndeterminate,
    headerLabel:
      pageIds.length > 0
        ? `Select all ${countLabel(pageIds.length, noun)} on this page`
        : `Select all ${noun.other} on this page`,
    togglePage,
    toggleRow,
    isChecked: (id) => checkedList.includes(idKey(id)),
    removeChecked,
    queueLabel: selectAllActionLabel(totalCount, noun),
    showQueueControl: totalCount > 0,
    allPendingSelected,
    selectingAll,
    selectAllError,
    selectQueue,
    clearSelection,
    clearLabel: CLEAR_SELECTION_LABEL,
    save,
    saving,
    saveError,
    clearSaveError: () => setSaveError(null),
    notice,
    dismissNotice: () => setNotice(null),
  };
}
